/**
 * Ustawienia → Konto → Pasek boczny — osobista konfiguracja wąskiego paska ikon (tryb LISTA).
 *
 * Zakres (decyzje Eugenii 2026-09-24, mieszanie menu customowych 2026-10-01):
 *  - KOLOROWE ikony (widoki menu) — przeciąganie kolejności + pokaż/ukryj; co najmniej jeden
 *    widok zostaje widoczny. Lista scala MODUŁY i MENU CUSTOMOWE z Kreatora w jednej osi, więc
 *    menu customowe da się wpleść między moduły i ukryć tak samo jak moduł;
 *  - SZARE skróty (Dashboard / Kalendarz / Mapa obszarów u góry, Użytkownicy / Firmy na dole) —
 *    tylko pokaż/ukryj, stałe miejsca; „Ustawienia" zablokowane (jedyne wejście do tego ekranu);
 *  - jawne „Zapisz" (bez autozapisu), „Anuluj" wraca do zapisanego stanu, „Przywróć domyślne"
 *    układa jak z uprawnień i pokazuje wszystko (zatwierdza dopiero Zapisz).
 * To warstwa wyglądu nad menu z uprawnień — lista widoków jest już przefiltrowana, ekran niczego
 * ponad uprawnienia nie odsłoni. Ukryty widok nadal jest osiągalny z wyszukiwarki i linku.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Announcements, DragEndEvent, UniqueIdentifier } from '@dnd-kit/core';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SYSTEM_ITEMS } from '@saas/shared';
import {
  Calendar,
  Eye,
  EyeOff,
  GripVertical,
  LayoutDashboard,
  Lock,
  type LucideIcon,
  Map as MapIcon,
  Package,
  RotateCcw,
} from 'lucide-react';

import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { IconButton } from '@/components/IconButton';
import {
  combinedRailColorLookup,
  type CustomRailView,
  resolveIcon,
  resolveRailViewIcon,
  useCustomRailViews,
  useTenantMenuViews,
} from '@/components/Sidebar';
import { Tooltip } from '@/components/Tooltip';
import { useToast } from '@/contexts/ToastContext';
import { useRailPreferences } from '@/hooks/useRailPreferences';
import {
  arrangeRailItems,
  RAIL_LOCKED_TILE_KEYS,
  type RailEntry,
  railPreferencesFromEntries,
  type RailTileKey,
  sameRailEntries,
} from '@/lib/rail-preferences';
import { cn } from '@/lib/utils';

type ViewEntry = RailEntry<CustomRailView>;

interface TileDef {
  key: RailTileKey;
  label: string;
  icon: LucideIcon;
}

function useTileDefs(): { top: TileDef[]; base: TileDef[] } {
  const { t } = useTranslation();
  return useMemo(() => {
    const top: TileDef[] = [
      { key: 'dashboard', label: t('tenant.sidebar.dashboard'), icon: LayoutDashboard },
      { key: 'calendar', label: t('tenant.calendar.title'), icon: Calendar },
      { key: 'area-map', label: t('tenant.sidebar.accessMap.title'), icon: MapIcon },
    ];
    const baseKeys: RailTileKey[] = ['users', 'companies', 'settings'];
    const base: TileDef[] = baseKeys.map((key) => {
      const def = SYSTEM_ITEMS.find((s) => s.itemKey === key);
      return {
        key,
        label: def ? t(def.labelI18nKey, def.defaultLabel) : key,
        icon: resolveIcon(def?.iconName, Package),
      };
    });
    return { top, base };
  }, [t]);
}

function sameSet(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  return a.size === b.size && [...a].every((k) => b.has(k));
}

export function RailSettingsTab() {
  const { t } = useTranslation();
  const toast = useToast();
  const lastVisibleToastId = useRef<string | null>(null);
  const { views: moduleViews, isLoading: viewsLoading } = useTenantMenuViews();
  const {
    customViews,
    isLoading: customLoading,
    isError: customError,
    retry: retryCustom,
  } = useCustomRailViews();
  const {
    preferences,
    isLoading: prefsLoading,
    isError: prefsError,
    retry: retryPrefs,
    save,
    isSaving,
  } = useRailPreferences();
  const tiles = useTileDefs();

  // Scalona lista widoków: moduły (naturalnie pierwsze) + menu customowe z Kreatora — JEDNA oś
  // przestawiania i ukrywania (decyzja Eugenii 2026-10-01). Kolory i ikony wspólne z prawdziwym
  // paskiem; menu customowe rozpoznawane po prefiksie kodu (`custom:`).
  const combinedViews = useMemo<CustomRailView[]>(
    () => [...moduleViews.map((v) => ({ ...v, colorVar: null })), ...customViews],
    [moduleViews, customViews],
  );
  const colorFor = useMemo(() => combinedRailColorLookup(combinedViews), [combinedViews]);

  // Stan zapisany (z ustawień) i domyślny (jak z uprawnień) — punkty odniesienia dla
  // „niezapisanych zmian" i przycisku „Przywróć domyślne".
  const savedEntries = useMemo(
    () => arrangeRailItems(combinedViews, preferences),
    [combinedViews, preferences],
  );
  const savedTiles = useMemo(() => new Set(preferences.hiddenTiles), [preferences.hiddenTiles]);
  const defaultEntries = useMemo<ViewEntry[]>(
    () => combinedViews.map((item) => ({ item, visible: true })),
    [combinedViews],
  );

  const [entries, setEntries] = useState<ViewEntry[]>(savedEntries);
  const [hiddenTiles, setHiddenTiles] = useState<Set<string>>(savedTiles);
  useEffect(() => setEntries(savedEntries), [savedEntries]);
  useEffect(() => setHiddenTiles(savedTiles), [savedTiles]);

  const dirty = !sameRailEntries(entries, savedEntries) || !sameSet(hiddenTiles, savedTiles);
  const isDefault = sameRailEntries(entries, defaultEntries) && hiddenTiles.size === 0;
  const visibleCount = entries.filter((e) => e.visible).length;

  // Komunikaty dla czytnika ekranu przy przeciąganiu — po polsku i z NAZWAMI widoków (domyślne
  // dnd-kit są po angielsku i czytają techniczne kody modułów).
  const dndAccessibility = useMemo(() => {
    const nameOf = (id: UniqueIdentifier) =>
      entries.find((e) => e.item.code === id)?.item.name ?? String(id);
    const positionOf = (id: UniqueIdentifier) => entries.findIndex((e) => e.item.code === id) + 1;
    const total = entries.length;
    const announcements: Announcements = {
      onDragStart: ({ active }) =>
        t('tenant.settings.rail.dnd.start', {
          name: nameOf(active.id),
          position: positionOf(active.id),
          total,
        }),
      onDragOver: ({ active, over }) =>
        over
          ? t('tenant.settings.rail.dnd.over', {
              name: nameOf(active.id),
              position: positionOf(over.id),
              total,
            })
          : undefined,
      onDragEnd: ({ active, over }) =>
        over
          ? t('tenant.settings.rail.dnd.end', {
              name: nameOf(active.id),
              position: positionOf(over.id),
              total,
            })
          : t('tenant.settings.rail.dnd.cancel', { name: nameOf(active.id) }),
      onDragCancel: ({ active }) =>
        t('tenant.settings.rail.dnd.cancel', { name: nameOf(active.id) }),
    };
    return {
      announcements,
      screenReaderInstructions: { draggable: t('tenant.settings.rail.dnd.instructions') },
    };
  }, [entries, t]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setEntries((list) => {
      const from = list.findIndex((e) => e.item.code === active.id);
      const to = list.findIndex((e) => e.item.code === over.id);
      return from < 0 || to < 0 ? list : arrayMove(list, from, to);
    });
  }

  function toggleView(code: string) {
    const entry = entries.find((e) => e.item.code === code);
    if (entry?.visible && visibleCount === 1) {
      // Pojedynczo — kolejne kliknięcie podmienia komunikat zamiast piętrzyć identyczne toasty.
      if (lastVisibleToastId.current) toast.dismiss(lastVisibleToastId.current);
      lastVisibleToastId.current = toast.show({
        variant: 'warning',
        title: t('tenant.settings.rail.lastVisible'),
      });
      return;
    }
    setEntries((list) =>
      list.map((e) => (e.item.code === code ? { ...e, visible: !e.visible } : e)),
    );
  }

  function toggleTile(key: RailTileKey) {
    if (RAIL_LOCKED_TILE_KEYS.includes(key)) return;
    setHiddenTiles((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function handleSave() {
    try {
      // Stan równy domyślnemu = czyścimy osobiste ustawienia (nowe moduły trafią wtedy na
      // swoje naturalne miejsce, nie na koniec).
      await save(isDefault ? null : railPreferencesFromEntries(entries, [...hiddenTiles]));
      toast.show({ variant: 'success', title: t('tenant.settings.rail.saved') });
    } catch {
      toast.show({ variant: 'error', title: t('tenant.settings.rail.saveError') });
    }
  }

  function handleCancel() {
    setEntries(savedEntries);
    setHiddenTiles(savedTiles);
  }

  function handleReset() {
    setEntries(defaultEntries);
    setHiddenTiles(new Set());
  }

  const loading = viewsLoading || customLoading || prefsLoading;

  // Nieudany odczyt ustawień albo listy menu customowych — bez edytora: pokazany układ nie byłby
  // układem użytkownika, a Zapisz nadpisałby jego prawdziwe ustawienia na serwerze (przy braku
  // menu customowych z zapisu wypadłyby ich ukrycia i miejsca między modułami).
  if (prefsError || customError) {
    return (
      <div className="flex max-w-2xl flex-col gap-4 pb-8" data-testid="rail-settings">
        <p className="text-sm text-text-secondary">{t('tenant.settings.rail.intro')}</p>
        <Alert
          tone="error"
          title={
            prefsError
              ? t('tenant.settings.rail.loadError')
              : t('tenant.settings.rail.customLoadError')
          }
          data-testid="rail-settings-load-error"
          actions={
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<RotateCcw className="h-4 w-4" />}
              onClick={() => {
                if (prefsError) void retryPrefs();
                if (customError) retryCustom();
              }}
              data-testid="rail-settings-retry"
            >
              {t('tenant.settings.rail.retry')}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 pb-8" data-testid="rail-settings">
      <p className="max-w-2xl text-sm text-text-secondary">{t('tenant.settings.rail.intro')}</p>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,560px)_auto]">
        <div className="flex flex-col gap-6">
          <RailSection title={t('tenant.settings.rail.sections.top')}>
            {tiles.top.map((tile) => (
              <TileRow
                key={tile.key}
                tile={tile}
                visible={!hiddenTiles.has(tile.key)}
                onToggle={() => toggleTile(tile.key)}
              />
            ))}
          </RailSection>

          <RailSection
            title={t('tenant.settings.rail.sections.views')}
            hint={t('tenant.settings.rail.sections.viewsHint')}
          >
            {loading ? (
              <div className="py-6 text-sm text-text-muted">{t('common.loading')}</div>
            ) : entries.length === 0 ? (
              <div className="py-6 text-sm text-text-muted">{t('tenant.settings.rail.empty')}</div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                accessibility={dndAccessibility}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={entries.map((e) => e.item.code)}
                  strategy={verticalListSortingStrategy}
                >
                  {entries.map((entry) => (
                    <SortableViewRow
                      key={entry.item.code}
                      entry={entry}
                      colorVar={colorFor(entry.item.code)}
                      onToggle={() => toggleView(entry.item.code)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </RailSection>

          <RailSection title={t('tenant.settings.rail.sections.base')}>
            {tiles.base.map((tile) => (
              <TileRow
                key={tile.key}
                tile={tile}
                visible={!hiddenTiles.has(tile.key)}
                locked={RAIL_LOCKED_TILE_KEYS.includes(tile.key)}
                onToggle={() => toggleTile(tile.key)}
              />
            ))}
          </RailSection>

          <div className="flex flex-wrap items-center gap-3 border-t border-border-secondary pt-4">
            <Button
              variant="secondary"
              leftIcon={<RotateCcw className="h-4 w-4" />}
              onClick={handleReset}
              disabled={isDefault}
              data-testid="rail-settings-reset"
            >
              {t('tenant.settings.rail.reset')}
            </Button>
            <span className="flex-1" />
            {dirty && (
              <span className="text-sm text-text-secondary" data-testid="rail-settings-dirty">
                {t('tenant.settings.rail.unsaved')}
              </span>
            )}
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={!dirty || isSaving}
              data-testid="rail-settings-cancel"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={!dirty}
              loading={isSaving}
              data-testid="rail-settings-save"
            >
              {t('common.save')}
            </Button>
          </div>
        </div>

        <RailPreview
          entries={entries}
          hiddenTiles={hiddenTiles}
          tiles={tiles}
          colorFor={colorFor}
        />
      </div>
    </div>
  );
}

function RailSection({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2">
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-[0.05em] text-text-muted">
          {title}
        </h3>
        {hint && <p className="mt-1 text-sm text-text-secondary">{hint}</p>}
      </div>
      <div className="flex flex-col gap-1.5">{children}</div>
    </section>
  );
}

const rowClass =
  'flex h-12 items-center gap-3 rounded-lg border border-border-secondary bg-bg-elevated px-3';

function VisibilityToggle({
  visible,
  label,
  onToggle,
}: {
  visible: boolean;
  label: string;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  return (
    <IconButton
      size="sm"
      aria-label={
        visible
          ? t('tenant.settings.rail.hide', { name: label })
          : t('tenant.settings.rail.show', { name: label })
      }
      aria-pressed={!visible}
      onClick={onToggle}
    >
      {visible ? <Eye /> : <EyeOff />}
    </IconButton>
  );
}

function SortableViewRow({
  entry,
  colorVar,
  onToggle,
}: {
  entry: ViewEntry;
  colorVar: string;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: entry.item.code,
  });
  const Icon = resolveRailViewIcon(entry.item.code, entry.item.icon);
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        rowClass,
        isDragging && 'relative z-10 border-accent-500 bg-accent-50 shadow-md',
      )}
      data-testid={`rail-settings-view-${entry.item.code}`}
    >
      <span
        {...attributes}
        {...listeners}
        aria-label={t('tenant.settings.rail.dragHandle', { name: entry.item.name })}
        className="flex h-7 w-5 cursor-grab touch-none items-center justify-center text-text-muted hover:text-text-primary active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4" />
      </span>
      <span
        className={cn(
          'flex h-8 w-8 flex-none items-center justify-center rounded-full text-white [&_svg]:h-[18px] [&_svg]:w-[18px]',
          !entry.visible && 'grayscale opacity-50',
        )}
        style={{ background: `rgb(${colorVar})` }}
      >
        <Icon />
      </span>
      <span
        className={cn(
          'flex-1 truncate text-sm font-medium',
          entry.visible ? 'text-text-primary' : 'text-text-muted',
        )}
      >
        {entry.item.name}
        {!entry.visible && (
          <span className="ml-2 text-xs font-normal">{t('tenant.settings.rail.hiddenBadge')}</span>
        )}
      </span>
      <VisibilityToggle visible={entry.visible} label={entry.item.name} onToggle={onToggle} />
    </div>
  );
}

function TileRow({
  tile,
  visible,
  locked = false,
  onToggle,
}: {
  tile: TileDef;
  visible: boolean;
  locked?: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const Icon = tile.icon;
  const shown = locked || visible;
  return (
    <div className={rowClass} data-testid={`rail-settings-tile-${tile.key}`}>
      {/* Wyrównanie z wierszami widoków (tam jest uchwyt do przeciągania). */}
      <span className="w-5" aria-hidden />
      <span
        className={cn(
          'flex h-8 w-8 flex-none items-center justify-center rounded-full bg-[#8f9bae] text-white [&_svg]:h-[18px] [&_svg]:w-[18px]',
          !shown && 'opacity-40',
        )}
      >
        <Icon />
      </span>
      <span
        className={cn(
          'flex-1 truncate text-sm font-medium',
          shown ? 'text-text-primary' : 'text-text-muted',
        )}
      >
        {tile.label}
        {!shown && (
          <span className="ml-2 text-xs font-normal">{t('tenant.settings.rail.hiddenBadge')}</span>
        )}
      </span>
      {locked ? (
        <Tooltip content={t('tenant.settings.rail.lockedHint')}>
          <span
            className="flex h-7 w-7 items-center justify-center text-text-muted"
            aria-label={t('tenant.settings.rail.lockedHint')}
            data-testid={`rail-settings-tile-${tile.key}-locked`}
          >
            <Lock className="h-4 w-4" />
          </span>
        </Tooltip>
      ) : (
        <VisibilityToggle visible={visible} label={tile.label} onToggle={onToggle} />
      )}
    </div>
  );
}

/** Podgląd paska na żywo — ten sam wygląd kafelków co prawdziwy pasek (klasy `rail-*`). */
function RailPreview({
  entries,
  hiddenTiles,
  tiles,
  colorFor,
}: {
  entries: ViewEntry[];
  hiddenTiles: ReadonlySet<string>;
  tiles: { top: TileDef[]; base: TileDef[] };
  colorFor: (code: string) => string;
}) {
  const { t } = useTranslation();
  const shownTile = (tile: TileDef) =>
    RAIL_LOCKED_TILE_KEYS.includes(tile.key) || !hiddenTiles.has(tile.key);
  const renderTile = (tile: TileDef) => {
    const Icon = tile.icon;
    return (
      <div key={tile.key} className="rail-tile" title={tile.label}>
        <Icon />
      </div>
    );
  };
  return (
    <div className="lg:sticky lg:top-4 lg:self-start" data-testid="rail-settings-preview">
      <div className="mb-2 text-center text-xs font-medium text-text-muted">
        {t('tenant.settings.rail.preview')}
      </div>
      <div className="mx-auto flex w-[72px] flex-col items-center gap-2 rounded-2xl border border-border-secondary bg-[#f1f1f4] py-4 shadow-sm dark:bg-bg-secondary">
        {tiles.top.filter(shownTile).map(renderTile)}
        <div className="h-3" />
        {entries
          .filter((e) => e.visible)
          .map((e) => {
            const Icon = resolveRailViewIcon(e.item.code, e.item.icon);
            return (
              <div
                key={e.item.code}
                className="rail-glass"
                style={{ '--c': colorFor(e.item.code) } as React.CSSProperties}
                title={e.item.name}
              >
                <Icon />
              </div>
            );
          })}
        <div className="h-3" />
        {tiles.base.filter(shownTile).map(renderTile)}
      </div>
    </div>
  );
}
